/**
 * Cache Manager for Assets SDK
 * Module-level persistent cache
 * Structure: Map<assetType, TypeCache>
 */
/*
 * This is a true module-level singleton:
 * - Lives at module scope (not on window/globalThis)
 * - Shared across all Assets instances in the SAME module
 * - Survives Assets instance disposal
 * - Survives component unmount/remount
 * - Only cleared on page reload
 */
const PERSISTENT_CACHE = new Map();
export class CacheManager {
    constructor(maxAssetsPerType = 2) {
        Object.defineProperty(this, "maxAssetsPerType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "receivedAssets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        }); // Track transferred assets for return
        this.maxAssetsPerType = maxAssetsPerType;
    }
    /**
     * Gets or creates a TypeCache for the given asset type
     */
    getOrCreateTypeCache(assetType) {
        let typeCache = PERSISTENT_CACHE.get(assetType);
        if (!typeCache) {
            typeCache = { assets: new Map() };
            PERSISTENT_CACHE.set(assetType, typeCache);
        }
        return typeCache;
    }
    /**
     * Checks if an asset exists in the cache
     */
    has(rxId, assetType) {
        const typeCache = PERSISTENT_CACHE.get(assetType);
        return typeCache?.assets.has(rxId) ?? false;
    }
    /**
     * Gets an asset from the cache
     */
    get(rxId, assetType) {
        const typeCache = PERSISTENT_CACHE.get(assetType);
        const asset = typeCache?.assets.get(rxId);
        return asset;
    }
    /**
     * Adds an asset to the cache with FIFO eviction based on maxAssetsPerType
     * @param rxId - The asset identifier
     * @param assetType - The type of asset
     * @param asset - The asset data
     * @param pin - When true, pins this asset to prevent eviction (only one pinned asset per type)
     */
    add(rxId, assetType, asset, pin) {
        const typeCache = this.getOrCreateTypeCache(assetType);
        const assetsMap = typeCache.assets;
        // FIFO eviction: if at capacity, remove oldest (first in Map) that is not pinned
        if (assetsMap.size >= this.maxAssetsPerType && !assetsMap.has(rxId)) {
            const keysArray = Array.from(assetsMap.keys());
            // Find the oldest non-pinned asset to evict
            let evictedRxId = null;
            for (const key of keysArray) {
                if (key !== typeCache.pinnedRxId) {
                    evictedRxId = key;
                    break;
                }
            }
            if (evictedRxId) {
                assetsMap.delete(evictedRxId);
            }
            else {
                // All assets are pinned (shouldn't happen with single pin), skip adding
                return;
            }
        }
        // Add the new asset (at end of Map = newest)
        assetsMap.set(rxId, asset);
        // Update pinned asset if pin option is set
        if (pin) {
            typeCache.pinnedRxId = rxId;
        }
    }
    /**
     * Deletes an asset from the cache (used when transferring)
     */
    delete(rxId, assetType) {
        const typeCache = PERSISTENT_CACHE.get(assetType);
        if (typeCache) {
            typeCache.assets.delete(rxId);
        }
    }
    /**
     * Tracks a received transferred asset for later return to parent
     */
    trackReceivedAsset(cacheKey, asset) {
        this.receivedAssets.set(cacheKey, asset);
    }
    /**
     * Gets all received assets that need to be returned
     */
    getReceivedAssets() {
        return this.receivedAssets;
    }
    /**
     * Clears tracked received assets (after they've been returned)
     */
    clearReceivedAssets() {
        this.receivedAssets.clear();
    }
    /**
     * Gets the count of received assets
     */
    getReceivedAssetsCount() {
        return this.receivedAssets.size;
    }
    /**
     * Pins an existing asset in the cache
     * @param rxId - The asset identifier to pin
     * @param assetType - The type of asset
     * @returns true if the asset was found and pinned, false otherwise
     */
    pin(rxId, assetType) {
        const typeCache = PERSISTENT_CACHE.get(assetType);
        if (typeCache?.assets.has(rxId)) {
            typeCache.pinnedRxId = rxId;
        }
    }
    /**
     * Clears all assets from the persistent cache
     * Primarily for testing purposes
     */
    clearAll() {
        PERSISTENT_CACHE.clear();
        this.receivedAssets.clear();
    }
}
//# sourceMappingURL=cache-manager.js.map